import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, withModifiers as _withModifiers, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["tabindex", "aria-pressed", "aria-label"]

import { computed, nextTick, ref } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from '@shell/composables/useI18n';
import type { TopLevelMenuCluster } from '@shell/components/nav/TopLevelMenu.helper';
import { reportPinWriteFailure } from '@shell/utils/cluster-pref-writer';

// Derived, not restated: every caller passes a `TopLevelMenuCluster`, so picking the fields keeps this
// control from silently drifting if one of their types changes in the helper.
type PinnableCluster = Pick<TopLevelMenuCluster, 'pinned' | 'label' | 'pin' | 'unpin'>;

interface Props {
  cluster: PinnableCluster;
  /** tabindex for the toggle (-1 removes it from the tab order on the collapsed rail; omit to leave unset). */
  tabOrder?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'Pinned',
  props: {
    cluster: {},
    tabOrder: {}
  },
  emits: ['pinned', 'unpinned'],
  setup(__props: any, { expose: __expose, emit: __emit }) {

// Allow the user to pin a cluster by clicking it.
const props = __props;
const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const pinned = computed(() => props.cluster.pinned);

// Restart the one-shot pop animation on each toggle: clear the class, wait a tick so Vue drops it from the
// DOM, then re-add so the animation replays — keeps the retrigger reactive with no direct DOM manipulation.
const popping = ref(false);

// Exposed so a surface that offers its own route to the pin — the header's Cmd+Shift+P / Alt+P — toggles
// THROUGH this control rather than writing the pref itself: one path for the write, the growl on failure
// and the pop, so the icon reacts to the shortcut exactly as it does to a click.
__expose({ toggle });

async function toggle() {
  const wasPinned = pinned.value;
  // `pin`/`unpin` return the serialized pref write; `reportPinWriteFailure` owns its failure contract
  // and the growl, so this surface and the switcher flyout cannot drift apart on it.
  const write = wasPinned ? props.cluster.unpin() : props.cluster.pin();

  reportPinWriteFailure(store, t, write);

  // Unpinning takes this control off the surface with it, so the surface has to be told before that
  // happens — it owns where focus lands next and what gets announced.
  emit(wasPinned ? 'unpinned' : 'pinned', props.cluster);

  popping.value = false;
  await nextTick();
  popping.value = true;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("button", {
    type: "button",
    tabindex: __props.tabOrder,
    "aria-pressed": !!pinned.value,
    class: _normalizeClass(["pin icon", { 'icon-pin': pinned.value, 'icon-pin-outlined': !pinned.value, 'is-pinned': pinned.value, 'pin-pop': popping.value }]),
    "aria-label": _unref(t)('nav.ariaLabel.pinCluster', { cluster: __props.cluster.label }),
    onClick: _withModifiers(toggle, ["stop","prevent"]),
    onAnimationend: _cache[0] || (_cache[0] = ($event: any) => (popping.value = false))
  }, null, 42, _hoisted_1))
}
}

})